import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { adminApi } from '../../services/api';
import SalesAgGridTable from '../../components/sales/SalesAgGridTable';
import JobSearchInstructions from '../../components/sales/JobSearchInstructions';
import { useOrganization } from '../../context/AuthContext';
import {
  LogoCellRenderer,
  BooleanCellRenderer,
  LongTextCellRenderer,
  JobTitleRenderer,
  CompanyNameRenderer,
  JobSourceRenderer,
  EmploymentTypeRenderer,
  LocationRenderer,
  dateFormatter
} from '../../components/sales/SalesCellRenderers';
import {
  setJobListings,
  setFilter,
  clearFilters,
  setLoading,
  setRefreshing,
  setError,
  selectJobListings,
  selectFilters,
  selectStats,
  selectLoading,
  selectRefreshing,
  selectError,
  selectLastFetched,
  selectLastSearchOffset,
} from '../../components/redux/slices/sales-module/jobListingsSlice';

const JobListingsPage = () => {
  const gridRef = useRef(null);
  const csvImportRef = useRef(null);
  const dispatch = useDispatch();
  const { activeOrganization } = useOrganization();

  // Redux state
  const jobListings = useSelector(selectJobListings);
  const filters = useSelector(selectFilters);
  const stats = useSelector(selectStats);
  const loading = useSelector(selectLoading);
  const refreshing = useSelector(selectRefreshing);
  const error = useSelector(selectError);
  const lastFetched = useSelector(selectLastFetched);
  const lastSearchOffset = useSelector(selectLastSearchOffset);

  // Local UI state
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [availableEndpoints, setAvailableEndpoints] = useState([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState('7d');
  const [currentEndpointName, setCurrentEndpointName] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [creatingProspects, setCreatingProspects] = useState(false);
  const [prospectCreationStatus, setProspectCreationStatus] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  // Fetch available endpoints
  useEffect(() => {
    const fetchEndpoints = async () => {
      try {
        const response = await adminApi.getJobEndpoints();
        if (response.data && response.data.success) {
          setAvailableEndpoints(response.data.data || []);
          setSelectedEndpoint(response.data.default || '7d');
        } else {
          const errorMsg = response.data?.error || response.data?.message || 'Failed to load endpoints';
          dispatch(setError(`Failed to load job endpoints: ${errorMsg}`));
        }
      } catch (err) {
        const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to load endpoints';
        dispatch(setError(`Failed to load job endpoints: ${errorMsg}`));
      }
    };
    fetchEndpoints();
  }, [dispatch]);

  // Fetch job listings from API
  const fetchJobListings = useCallback(async (showLoader = true, endpoint = selectedEndpoint, filterOverrides = {}) => {
    try {
      if (showLoader) {
        dispatch(setLoading(true));
      } else {
        dispatch(setRefreshing(true));
      }
      dispatch(setError(null));

      // Build query params from filters
      const queryParams = {
        limit,
        ...(offset > 0 && { offset }),
        endpoint: endpoint,
        ...filterOverrides,
      };

      // Add filter parameters if they have values
      if (filters.search) queryParams.search = filters.search;
      if (filters.location) queryParams.location = filters.location;
      if (filters.remote !== null) queryParams.remote = filters.remote;
      if (filters.source) queryParams.source = filters.source;
      if (filters.sourceExclusion) queryParams.source_exclusion = filters.sourceExclusion;
      if (filters.advancedOrganization) queryParams.advanced_organization_filter = filters.advancedOrganization;
      if (filters.dateFilter) queryParams.date_filter = filters.dateFilter;

      // AI Filters
      if (filters.aiEmploymentType) queryParams.ai_employment_type_filter = filters.aiEmploymentType;
      if (filters.aiWorkArrangement) queryParams.ai_work_arrangement_filter = filters.aiWorkArrangement;
      if (filters.aiExperienceLevel) queryParams.ai_experience_level_filter = filters.aiExperienceLevel;
      if (filters.aiHasSalary !== null) queryParams.ai_has_salary = filters.aiHasSalary;
      if (filters.aiTaxonomies) queryParams.ai_taxonomies_a_filter = filters.aiTaxonomies;
      if (filters.aiVisaSponsorship !== null) queryParams.ai_visa_sponsorship_filter = filters.aiVisaSponsorship;

      // LinkedIn Filters
      if (filters.liIndustry) queryParams.li_industry_filter = filters.liIndustry;
      if (filters.liOrganizationSlug) queryParams.li_organization_slug_filter = filters.liOrganizationSlug;
      if (filters.agency !== null) queryParams.agency = filters.agency;

      // Fetch job listings from new API with selected endpoint
      const response = await adminApi.getJobListings(queryParams);

      if (response.data && response.data.success) {
        const jobs = response.data.data || [];
        dispatch(setJobListings({ jobs, offset }));
        setCurrentEndpointName(response.data.endpoint_name || '');
      } else {
        const errorMsg = response.data?.error || response.data?.message || 'No data received from server';
        dispatch(setError(`Failed to load jobs: ${errorMsg}`));
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to fetch job listings';
      dispatch(setError(`Error loading jobs: ${errorMsg}`));
    } finally {
      dispatch(setLoading(false));
      dispatch(setRefreshing(false));
    }
  }, [dispatch, selectedEndpoint, filters, limit, offset]);

 
  // Handle endpoint change - just update the selection, don't auto-fetch
  const handleEndpointChange = useCallback((endpoint) => {
    setSelectedEndpoint(endpoint);
    dispatch(setError(null));
  }, [dispatch]);

  // Handle filter changes - just update filter state
  const handleFilterChange = useCallback((filterName, value) => {
    dispatch(setFilter({ filterName, value }));
  }, [dispatch]);

  // Handle apply filters button - triggers new API fetch with current filters
  const handleApplyFilters = useCallback(() => {
    fetchJobListings(false);
  }, [fetchJobListings]);

  // Handle clear all filters - just resets filters, doesn't refetch
  const handleClearFilters = useCallback(() => {
    dispatch(clearFilters());
  }, [dispatch]);

  // Parse a single CSV row, handling quoted fields and escaped quotes
  const parseCSVRow = (row) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < row.length; i++) {
      const char = row[i];
      if (char === '"') {
        if (inQuotes && row[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (char === ',' && !inQuotes) {
        result.push(current); current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  // Handle CSV file import
  const handleCsvImport = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const lines = event.target.result.split(/\r?\n/).filter(l => l.trim());
        if (lines.length < 2) { dispatch(setError('CSV has no data rows')); return; }
        const headers = parseCSVRow(lines[0]);
        const jobs = [];
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVRow(lines[i]);
          if (!values.some(v => v)) continue;
          const job = {};
          headers.forEach((header, idx) => {
            const val = values[idx] ?? '';
            job[header] = header === 'remote'
              ? (val === 'true' ? true : val === 'false' ? false : null)
              : val;
          });
          if (!job.id) job.id = `csv-${i}`;
          jobs.push(job);
        }
        dispatch(setJobListings({ jobs, offset: 0 }));
        setCurrentEndpointName('CSV Import');
      } catch (err) {
        dispatch(setError(`Failed to parse CSV: ${err.message}`));
      }
      e.target.value = ''; // reset so same file can be re-imported
    };
    reader.readAsText(file);
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle row selection changes
  const onSelectionChanged = useCallback(() => {
    if (gridRef.current && gridRef.current.api) {
      const selectedNodes = gridRef.current.api.getSelectedNodes();
      const selectedData = selectedNodes.map(node => node.data);
      setSelectedRows(selectedData);
    }
  }, []);

  // Handle adding selected jobs to sales leads
  const handleAddToProspects = useCallback(async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one job to add to sales leads');
      return;
    }

    if (!window.confirm(`Add ${selectedRows.length} job(s) to sales leads?`)) {
      return;
    }

    setCreatingProspects(true);
    setProspectCreationStatus({ success: 0, failed: 0, errors: [] });

    let successCount = 0;
    let failedCount = 0;
    const errors = [];

    for (const job of selectedRows) {
      try {
        // Prepare lead data from job listing - store ALL available fields
        const leadData = {
          external_id: job.id, // Use job ID to prevent duplicates
          lead_data: {
            // Basic job fields
            id: job.id,
            job_title: job.job_title,
            company: job.company,
            company_object: job.company_object,
            url: job.url,
            location: job.location,
            short_location: job.short_location,
            remote: job.remote,
            description: job.description,
            employment_type: job.employment_type,
            date_posted: job.date_posted,
            date_created: job.date_created,
            date_validthrough: job.date_validthrough,

            // Salary fields
            salary_string: job.salary_string,
            min_salary: job.min_salary,
            max_salary: job.max_salary,
            currency: job.currency,

            // Source fields
            source: job.source,
            source_domain: job.source_domain,
            source_type: job.source_type,

            // Locations array
            locations: job.locations,

            // AI-enriched data (all fields)
            ai_salary_currency: job.ai_salary_currency,
            ai_salary_value: job.ai_salary_value,
            ai_salary_minvalue: job.ai_salary_minvalue,
            ai_salary_maxvalue: job.ai_salary_maxvalue,
            ai_salary_unittext: job.ai_salary_unittext,
            ai_benefits: job.ai_benefits,
            ai_experience_level: job.ai_experience_level,
            ai_work_arrangement: job.ai_work_arrangement,
            ai_work_arrangement_office_days: job.ai_work_arrangement_office_days,
            ai_remote_location: job.ai_remote_location,
            ai_remote_location_derived: job.ai_remote_location_derived,
            ai_key_skills: job.ai_key_skills,
            ai_hiring_manager_name: job.ai_hiring_manager_name,
            ai_hiring_manager_email_address: job.ai_hiring_manager_email_address,
            ai_core_responsibilities: job.ai_core_responsibilities,
            ai_requirements_summary: job.ai_requirements_summary,
            ai_working_hours: job.ai_working_hours,
            ai_employment_type: job.ai_employment_type,
            ai_job_language: job.ai_job_language,
            ai_visa_sponsorship: job.ai_visa_sponsorship,
            ai_taxonomies_a: job.ai_taxonomies_a,
            ai_keywords: job.ai_keywords,
            ai_education_requirements: job.ai_education_requirements,
            ai_has_salary: job.ai_has_salary,

            // LinkedIn company data (all fields)
            linkedin_id: job.linkedin_id,
            linkedin_org_employees: job.linkedin_org_employees,
            linkedin_org_url: job.linkedin_org_url,
            linkedin_org_size: job.linkedin_org_size,
            linkedin_org_slogan: job.linkedin_org_slogan,
            linkedin_org_industry: job.linkedin_org_industry,
            linkedin_org_followers: job.linkedin_org_followers,
            linkedin_org_headquarters: job.linkedin_org_headquarters,
            linkedin_org_type: job.linkedin_org_type,
            linkedin_org_foundeddate: job.linkedin_org_foundeddate,
            linkedin_org_specialties: job.linkedin_org_specialties,
            linkedin_org_locations: job.linkedin_org_locations,
            linkedin_org_description: job.linkedin_org_description,
            linkedin_org_recruitment_agency_derived: job.linkedin_org_recruitment_agency_derived,
            linkedin_org_slug: job.linkedin_org_slug,
          },
          notes: `Imported from Job Listings (${currentEndpointName || 'Job API'}) on ${new Date().toLocaleDateString()}`
        };

        // Add organization if available
        if (activeOrganization?.id) {
          leadData.organization = activeOrganization.id;
        }

        await adminApi.createSalesLead(leadData);
        successCount++;
      } catch (err) {
        failedCount++;
        const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';
        errors.push({
          job_title: job.job_title,
          company: job.company,
          error: errorMsg
        });
      }
    }

    setProspectCreationStatus({
      success: successCount,
      failed: failedCount,
      errors: errors
    });
    setCreatingProspects(false);

    // Clear selection after successful creation
    if (successCount > 0 && gridRef.current && gridRef.current.api) {
      gridRef.current.api.deselectAll();
      setSelectedRows([]);
    }
  }, [selectedRows, currentEndpointName]);

  // Custom cell renderer for action buttons (job-specific)
  const ActionsCellRenderer = (props) => {
    const job = props.data;

    return (
      <div className="d-flex gap-1">
        <button
          className="btn btn-sm btn-primary"
          onClick={() => {
            setSelectedJob(job);
            setShowJobDetails(true);
          }}
          title="View Details"
        >
          <i className="fas fa-eye"></i>
        </button>
        {job.url && (
          <a
            href={job.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-success"
            title="View Job Posting"
          >
            <i className="fas fa-external-link-alt"></i>
          </a>
        )}
      </div>
    );
  };

  // Column definitions
  const columnDefs = useMemo(() => [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      pinned: 'left',
      width: 50,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressMenu: true
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: ActionsCellRenderer,
      pinned: 'left',
      width: 120,
      sortable: false,
      filter: false,
      suppressMovable: true
    },
    {
      headerName: 'Job Title',
      field: 'job_title',
      cellRenderer: JobTitleRenderer,
      width: 400,
      pinned: 'left',
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Logo',
      field: 'company_object.logo',
      cellRenderer: LogoCellRenderer,
      width: 80,
      sortable: false,
      filter: false
    },
    {
      headerName: 'Company',
      field: 'company',
      cellRenderer: CompanyNameRenderer,
      width: 200,
      filter: 'agSetColumnFilter'
    },
    {
      headerName: 'Location',
      field: 'short_location',
      cellRenderer: LocationRenderer,
      width: 180,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Remote',
      field: 'remote',
      cellRenderer: BooleanCellRenderer,
      width: 100,
      filter: 'agSetColumnFilter'
    },
    {
      headerName: 'Employment Type',
      field: 'employment_type',
      cellRenderer: EmploymentTypeRenderer,
      width: 150,
      filter: 'agSetColumnFilter'
    },
    {
      headerName: 'Date Posted',
      field: 'date_posted',
      width: 120,
      valueFormatter: dateFormatter,
      filter: 'agDateColumnFilter'
    },
    {
      headerName: 'Source',
      field: 'source',
      cellRenderer: JobSourceRenderer,
      width: 180,
      filter: 'agSetColumnFilter'
    },
    {
      headerName: 'Description',
      field: 'description',
      cellRenderer: LongTextCellRenderer,
      width: 400,
      tooltipField: 'description',
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'ID',
      field: 'id',
      width: 150,
      filter: 'agTextColumnFilter'
    }
  ], []);

  // Default column definitions
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 90,
    suppressMovable: false
  }), []);

  if (loading) {
    return (
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading job listings...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-sm-0 font-size-18">Job Listings
            {currentEndpointName && (
              <span className="badge bg-primary ms-2">{currentEndpointName}</span>
            )}</h4>
          <p className="text-muted mb-0">
            Browse job postings from multiple sources
          </p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <button
            className="btn btn-outline-secondary"
            onClick={() => setShowInstructions(!showInstructions)}
            title="Instructions for Job Search"
          >
            <i className={`fas fa-question-circle me-2`}></i>
            Instructions
            <i className={`fas fa-chevron-${showInstructions ? 'up' : 'down'} ms-2`}></i>
          </button>
          {/* <span className="text-secondary text-lg ml-3">FEEDS</span> */}
          {/* <button
            className="btn btn-outline-primary"
            onClick={() => fetchJobListings(false)}
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Refreshing...
              </>
            ) : ( <i className="fas fa-sync-alt"></i>
            )}
          </button> */}
        </div>
      </div>

      {/* Instructions Panel */}
      <JobSearchInstructions show={showInstructions} onClose={() => setShowInstructions(false)} />

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger text-black alert-dismissible fade show mb-4" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* Lead Creation Status */}
      {prospectCreationStatus && (
        <div className={`alert ${prospectCreationStatus.failed > 0 ? 'alert-warning' : 'alert-success'} alert-dismissible fade show mb-4`} role="alert">
          <div className="d-flex align-items-start">
            <div className="flex-grow-1">
              <h5 className="alert-heading mb-2">
                <i className={`fas ${prospectCreationStatus.failed > 0 ? 'fa-exclamation-triangle' : 'fa-check-circle'} me-2`}></i>
                Sales Lead Creation Results
              </h5>
              <p className="mb-2">
                <strong>Success:</strong> {prospectCreationStatus.success} lead(s) created
                {prospectCreationStatus.failed > 0 && (
                  <>
                    <br />
                    <strong>Failed:</strong> {prospectCreationStatus.failed} lead(s) failed
                  </>
                )}
              </p>
              {prospectCreationStatus.errors.length > 0 && (
                <div className="mt-2">
                  <strong>Errors:</strong>
                  <ul className="mb-0 mt-1" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {prospectCreationStatus.errors.map((err, idx) => (
                      <li key={idx}>
                        <strong>{err.job_title}</strong> at {err.company}: {err.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={() => setProspectCreationStatus(null)}
              aria-label="Close"
            ></button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {/* <div className="row mb-4">
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Total Jobs</h6>
                  <h3 className="mb-0">{stats.total}</h3>
                </div>
                <i className="fas fa-briefcase fa-2x text-primary"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Remote Jobs</h6>
                  <h3 className="mb-0">{stats.remote}</h3>
                </div>
                <i className="fas fa-home fa-2x text-success"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Sources</h6>
                  <h3 className="mb-0">{Object.keys(stats.bySource).length}</h3>
                </div>
                <i className="fas fa-globe fa-2x text-info"></i>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Filters Card */}
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center pb-0 border-0">
          <span className="text-lg text-primary">Search</span>
            {/* <i className="fas fa-search me-2"></i> */}
            
          <div className="d-flex d-flex-inline gap-2">
            <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            Advanced Filters
            <i className={`fas fa-chevron-${showAdvancedFilters ? 'up' : 'down'} ml-2`}></i>
          </button>
          <select
            className="form-select"
            style={{ width: 'auto', minWidth: '220px' }}
            value={selectedEndpoint}
            title="Available Job Feeds"
            onChange={(e) => handleEndpointChange(e.target.value)}
            disabled={loading || refreshing}
          >
            {availableEndpoints.map((endpoint) => (
              <option key={endpoint.id} value={endpoint.id} title={endpoint.description}>
                {endpoint.name}
                {endpoint.plan_required !== 'Basic' && ` (${endpoint.plan_required})`}
              </option>
            ))}
          </select>
          </div>
        </div>
        <div className="card-body">
          {/* Basic Filters */}
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Job Keyword *</label>
              <input
                type="text"
                className="form-control"
                placeholder="Job title, company, description..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Location</label>
              <input
                type="text"
                className="form-control"
                placeholder="City, state..."
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">
                Advanced Company Filter
                <i
                  className="fas fa-question-circle ms-1 text-muted"
                  title="Use operators: | (OR), & (AND), ! (NOT), :* (prefix). Example: Google | Microsoft | Amazon or University & ! Harvard"
                  style={{cursor: 'help', fontSize: '0.9em'}}
                ></i>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., Google | Microsoft | Amazon"
                value={filters.advancedOrganization}
                onChange={(e) => handleFilterChange('advancedOrganization', e.target.value)}
                title="Advanced filter with operators: | (OR), & (AND), ! (NOT), :* (prefix wildcard)"
              />
            </div>
            <div className="col-md-1">
              <label className="form-label">Results Limit</label>
              <select
                className="form-select"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                disabled={refreshing}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
              </select>
            </div>
            <div className="col-md-1">
              <label className="form-label">Offset (Skip)</label>
              <select
                className="form-select"
                value={offset}
                onChange={(e) => setOffset(Math.max(0, Number(e.target.value)))}
                disabled={refreshing}
              >
                <option value={0}>No</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Employment Type</label>
              <select
                className="form-select"
                value={filters.employmentType}
                onChange={(e) => handleFilterChange('employmentType', e.target.value)}
              >
                <option value="">All</option>
                {Object.keys(stats.byEmploymentType).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end gap-2">
              <button
                className="btn btn-primary"
                onClick={handleApplyFilters}
                disabled={refreshing || !filters.search.trim()}
              >
                <i className="fas fa-search me-2"></i>
                Search
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={handleClearFilters}
                disabled={refreshing}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Advanced Filters Section */}
          {showAdvancedFilters && (
            <>
              <hr className="my-4" />
              <h6 className="mb-3">
                <i className="fas fa-robot me-2 text-primary"></i>
                AI-Enriched Filters (BETA)
              </h6>
              <div className="row g-3 mb-4">
                <div className="col-md-2">
                  <label className="form-label">Remote</label>
                  <select
                    className="form-select"
                    value={filters.remote === null ? '' : filters.remote}
                    onChange={(e) => handleFilterChange('remote', e.target.value === '' ? null : e.target.value === 'true')}
                  >
                    <option value="">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Work Arrangement</label>
                  <select
                    className="form-select"
                    value={filters.aiWorkArrangement}
                    onChange={(e) => handleFilterChange('aiWorkArrangement', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="On-site">On-site</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="Remote OK">Remote OK</option>
                    <option value="Remote Solely">Remote Solely</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Experience Level</label>
                  <select
                    className="form-select"
                    value={filters.aiExperienceLevel}
                    onChange={(e) => handleFilterChange('aiExperienceLevel', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="0-2">0-2 years</option>
                    <option value="2-5">2-5 years</option>
                    <option value="5-10">5-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Has Salary</label>
                  <select
                    className="form-select"
                    value={filters.aiHasSalary === null ? '' : filters.aiHasSalary}
                    onChange={(e) => handleFilterChange('aiHasSalary', e.target.value === '' ? null : e.target.value === 'true')}
                  >
                    <option value="">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Visa Sponsorship</label>
                  <select
                    className="form-select"
                    value={filters.aiVisaSponsorship === null ? '' : filters.aiVisaSponsorship}
                    onChange={(e) => handleFilterChange('aiVisaSponsorship', e.target.value === '' ? null : e.target.value === 'true')}
                  >
                    <option value="">All</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">AI Employment Type</label>
                  <select
                    className="form-select"
                    value={filters.aiEmploymentType}
                    onChange={(e) => handleFilterChange('aiEmploymentType', e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="FULL_TIME">Full Time</option>
                    <option value="PART_TIME">Part Time</option>
                    <option value="CONTRACTOR">Contractor</option>
                    <option value="TEMPORARY">Temporary</option>
                    <option value="INTERN">Intern</option>
                    <option value="VOLUNTEER">Volunteer</option>
                    <option value="PER_DIEM">Per Diem</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Job Category (Taxonomy)</label>
                  <select
                    className="form-select"
                    value={filters.aiTaxonomies}
                    onChange={(e) => handleFilterChange('aiTaxonomies', e.target.value)}
                  >
                    <option value="">All</option>
                    {Object.keys(stats.byAiTaxonomies).sort().map(taxonomy => (
                      <option key={taxonomy} value={taxonomy}>
                        {taxonomy} ({stats.byAiTaxonomies[taxonomy]})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <hr className="my-3" />
              <h6 className="mb-3">
                <i className="fab fa-linkedin me-2 text-info"></i>
                LinkedIn Company Filters (BETA)
              </h6>
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <label className="form-label">Industry</label>
                  <select
                    className="form-select"
                    value={filters.liIndustry}
                    onChange={(e) => handleFilterChange('liIndustry', e.target.value)}
                  >
                    <option value="">All</option>
                    {Object.keys(stats.byLinkedInIndustry).sort().map(industry => (
                      <option key={industry} value={industry}>
                        {industry} ({stats.byLinkedInIndustry[industry]})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Company Slug</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., netflix, walmart"
                    value={filters.liOrganizationSlug}
                    onChange={(e) => handleFilterChange('liOrganizationSlug', e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Agency Filter</label>
                  <select
                    className="form-select"
                    value={filters.agency === null ? '' : filters.agency}
                    onChange={(e) => handleFilterChange('agency', e.target.value === '' ? null : e.target.value === 'true')}
                  >
                    <option value="">All</option>
                    <option value="false">Direct Employers</option>
                    <option value="true">Agencies Only</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Date Posted (After)</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.dateFilter}
                    onChange={(e) => handleFilterChange('dateFilter', e.target.value)}
                  />
                </div>
              </div>

              <hr className="my-3" />
              <h6 className="mb-3">
                <i className="fas fa-server me-2 text-secondary"></i>
                Source Filters
              </h6>
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Source (ATS)</label>
                  <select
                    className="form-select"
                    value={filters.source}
                    onChange={(e) => handleFilterChange('source', e.target.value)}
                  >
                    <option value="">All</option>
                    {Object.keys(stats.bySource).sort().map(source => (
                      <option key={source} value={source}>
                        {source} ({stats.bySource[source]})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Exclude Source</label>
                  <select
                    className="form-select"
                    value={filters.sourceExclusion}
                    onChange={(e) => handleFilterChange('sourceExclusion', e.target.value)}
                  >
                    <option value="">None</option>
                    {Object.keys(stats.bySource).sort().map(source => (
                      <option key={source} value={source}>
                        {source}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* AG Grid Table */}
      <div className="card">
        <div className="card-body p-0" style={{border:0}}>
          <div className="d-flex align-items-center justify-content-between p-2">
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-success btn-sm"
                onClick={handleAddToProspects}
                disabled={selectedRows.length === 0 || creatingProspects}
                title={selectedRows.length === 0 ? 'Select jobs to add to sales leads' : `Add ${selectedRows.length} job(s) to sales leads`}
              >
                {creatingProspects ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Creating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-user-plus me-2"></i>
                    Add to Sales Leads {selectedRows.length > 0 && `(${selectedRows.length})`}
                  </>
                )}
              </button>
              {selectedRows.length > 0 && (
              <div className="btn-group btn-group-sm">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => gridRef.current?.api?.exportDataAsCsv({
                    onlySelected: true,
                    fileName: `ankr-job-listings-${new Date().toISOString().slice(0, 10)}.csv`,
                    columnKeys: ['job_title', 'company', 'short_location', 'remote', 'employment_type', 'date_posted', 'source', 'source_domain', 'url', 'id'],
                    processHeaderCallback: (params) => params.column.getColDef().field || params.column.getColId(),
                  })}
                  disabled={selectedRows.length === 0}
                  title="Export selected rows to CSV"
                >
                  <i className="fas fa-file-csv me-1"></i>
                  Save as CSV
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => gridRef.current?.api?.exportDataAsExcel({
                    onlySelected: true,
                    fileName: `ankr-job-listings-${new Date().toISOString().slice(0, 10)}.xlsx`,
                    columnKeys: ['job_title', 'company', 'short_location', 'remote', 'employment_type', 'date_posted', 'source', 'source_domain', 'url', 'id'],
                  })}
                  disabled={selectedRows.length === 0}
                  title="Export selected rows to Excel"
                >
                  <i className="fas fa-file-excel me-1"></i>
                  Save as Excel
                </button>
              </div>)}        
            {/* <div className="vr"></div> */}
            {lastFetched && jobListings.length > 0 && (
              <span className="text-muted small mx-2">
                <i className="fas fa-check-circle text-success me-1"></i>
                {lastSearchOffset > 0
                  ? <>Showing <strong>{lastSearchOffset + 1}–{lastSearchOffset + stats.total}</strong>&nbsp;({stats.total} result{stats.total !== 1 ? 's' : ''})</>
                  : <>Showing <strong>{stats.total}</strong> result{stats.total !== 1 ? 's' : ''}</>
                }
              </span>
            )}
            </div>    
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => csvImportRef.current?.click()}
                title="Load jobs from a CSV file"
              >
                <i className="fas fa-file-upload me-1"></i>
                Import Saved Jobs
              </button>

          </div>
          {!loading && !refreshing && jobListings.length === 0 ? (
            <div className="d-flex flex-column align-items-center justify-content-center text-muted py-5">
              <i className={`fas ${lastFetched ? 'fa-search' : 'fa-briefcase'} fa-3x mb-3`}></i>
              <h5>{lastFetched ? 'No jobs found' : 'No jobs loaded yet'}</h5>
              <p className="mb-0">
                {lastFetched
                  ? 'Try adjusting your filters or selecting a different job feed.'
                  : 'Select a job feed and click Search to load job listings.'}
              </p>
            </div>
          ) : (
            <SalesAgGridTable
              ref={gridRef}
              rowData={jobListings}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              getRowId={(params) => String(params.data.id)}
              immutableData={true}
              pagination={true}
              paginationPageSize={50}
              paginationPageSizeSelector={[25, 50, 100, 200]}
              enableRangeSelection={true}
              enableCharts={true}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onSelectionChanged={onSelectionChanged}
            />
          )}
        </div>
      </div>

      {/* Job Details Modal */}
      {showJobDetails && selectedJob && (
        <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedJob.job_title}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowJobDetails(false);
                    setSelectedJob(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {/* Basic Info */}
                <h6 className="border-bottom pb-2 mb-3 text-primary">Basic Information</h6>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>ID:</strong> {selectedJob.id || 'N/A'}
                  </div>
                  <div className="col-md-6">
                    <strong>Company:</strong> {selectedJob.company || 'N/A'}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Location:</strong> {selectedJob.location || 'N/A'}
                  </div>
                  <div className="col-md-6">
                    <strong>Short Location:</strong> {selectedJob.short_location || 'N/A'}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <strong>Employment Type:</strong> {selectedJob.employment_type || 'N/A'}
                  </div>
                  <div className="col-md-4">
                    <strong>Remote:</strong> {selectedJob.remote ? 'Yes' : 'No'}
                  </div>
                  <div className="col-md-4">
                    <strong>Date Posted:</strong> {selectedJob.date_posted ? new Date(selectedJob.date_posted).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <strong>Date Created:</strong> {selectedJob.date_created ? new Date(selectedJob.date_created).toLocaleDateString() : 'N/A'}
                  </div>
                  <div className="col-md-4">
                    <strong>Valid Through:</strong> {selectedJob.date_validthrough ? new Date(selectedJob.date_validthrough).toLocaleDateString() : 'N/A'}
                  </div>
                  <div className="col-md-4">
                    <strong>Source:</strong> {selectedJob.source || 'N/A'}
                  </div>
                </div>
                <div className="row mb-4">
                  <div className="col-md-6">
                    <strong>Source Domain:</strong> {selectedJob.source_domain || 'N/A'}
                  </div>
                  <div className="col-md-6">
                    <strong>Source Type:</strong> {selectedJob.source_type || 'N/A'}
                  </div>
                </div>

                {/* Salary Info */}
                <h6 className="border-bottom pb-2 mb-3 text-primary">Salary Information</h6>
                <div className="row mb-4">
                  <div className="col-md-6">
                    <strong>Salary String:</strong> {selectedJob.salary_string || 'N/A'}
                  </div>
                  <div className="col-md-6">
                    <strong>Min/Max Salary:</strong> {selectedJob.min_salary && selectedJob.max_salary ? `${selectedJob.currency || ''} ${selectedJob.min_salary} - ${selectedJob.max_salary}` : 'N/A'}
                  </div>
                </div>

                {/* Company Details */}
                <h6 className="border-bottom pb-2 mb-3 text-primary">Company Details</h6>
                {selectedJob.company_object && (
                  <div className="row mb-4">
                    <div className="col-md-6">
                      <strong>Company Name:</strong> {selectedJob.company_object.name || 'N/A'}
                    </div>
                    <div className="col-md-6">
                      <strong>Domain:</strong> {selectedJob.company_object.domain || 'N/A'}
                    </div>
                    <div className="col-md-6">
                      <strong>URL:</strong> {selectedJob.company_object.url ? <a href={selectedJob.company_object.url} target="_blank" rel="noopener noreferrer">{selectedJob.company_object.url}</a> : 'N/A'}
                    </div>
                    <div className="col-md-6">
                      <strong>Industry:</strong> {selectedJob.company_object.industry || 'N/A'}
                    </div>
                    <div className="col-md-6">
                      <strong>Employee Count:</strong> {selectedJob.company_object.employee_count || 'N/A'}
                    </div>
                    {selectedJob.company_object.logo && (
                      <div className="col-md-6">
                        <strong>Logo:</strong> <img src={selectedJob.company_object.logo} alt="Logo" style={{maxHeight: '40px'}} onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                  </div>
                )}

                {/* LinkedIn Data */}
                <h6 className="border-bottom pb-2 mb-3 text-primary">LinkedIn Company Data</h6>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>LinkedIn ID:</strong> {selectedJob.linkedin_id || 'N/A'}
                  </div>
                  <div className="col-md-6">
                    <strong>LinkedIn URL:</strong> {selectedJob.linkedin_org_url ? <a href={selectedJob.linkedin_org_url} target="_blank" rel="noopener noreferrer">View</a> : 'N/A'}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Industry:</strong> {selectedJob.linkedin_org_industry || 'N/A'}
                  </div>
                  <div className="col-md-6">
                    <strong>Employees:</strong> {selectedJob.linkedin_org_employees || 'N/A'}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Org Size:</strong> {selectedJob.linkedin_org_size || 'N/A'}
                  </div>
                  <div className="col-md-6">
                    <strong>Founded:</strong> {selectedJob.linkedin_org_foundeddate || 'N/A'}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Type:</strong> {selectedJob.linkedin_org_type || 'N/A'}
                  </div>
                  <div className="col-md-6">
                    <strong>Followers:</strong> {selectedJob.linkedin_org_followers || 'N/A'}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Headquarters:</strong> {selectedJob.linkedin_org_headquarters || 'N/A'}
                  </div>
                  <div className="col-md-6">
                    <strong>Company Slug:</strong> {selectedJob.linkedin_org_slug || 'N/A'}
                  </div>
                </div>
                {selectedJob.linkedin_org_slogan && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <strong>Slogan:</strong> {selectedJob.linkedin_org_slogan}
                    </div>
                  </div>
                )}
                {selectedJob.linkedin_org_specialties && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <strong>Specialties:</strong> {selectedJob.linkedin_org_specialties}
                    </div>
                  </div>
                )}
                {selectedJob.linkedin_org_description && (
                  <div className="row mb-4">
                    <div className="col-12">
                      <strong>Description:</strong> <div className="text-muted small">{selectedJob.linkedin_org_description}</div>
                    </div>
                  </div>
                )}

                {/* AI-Enriched Data */}
                <h6 className="border-bottom pb-2 mb-3 text-primary">AI-Enriched Data</h6>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <strong>Experience Level:</strong> {selectedJob.ai_experience_level || 'N/A'}
                  </div>
                  <div className="col-md-4">
                    <strong>Work Arrangement:</strong> {selectedJob.ai_work_arrangement || 'N/A'}
                  </div>
                  <div className="col-md-4">
                    <strong>Employment Type:</strong> {selectedJob.ai_employment_type || 'N/A'}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-4">
                    <strong>AI Salary:</strong> {selectedJob.ai_salary_currency && selectedJob.ai_salary_value ? `${selectedJob.ai_salary_currency} ${selectedJob.ai_salary_value}` : 'N/A'}
                  </div>
                  <div className="col-md-4">
                    <strong>Min/Max:</strong> {selectedJob.ai_salary_minvalue && selectedJob.ai_salary_maxvalue ? `${selectedJob.ai_salary_minvalue} - ${selectedJob.ai_salary_maxvalue}` : 'N/A'}
                  </div>
                  <div className="col-md-4">
                    <strong>Salary Unit:</strong> {selectedJob.ai_salary_unittext || 'N/A'}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Visa Sponsorship:</strong> {selectedJob.ai_visa_sponsorship ? 'Yes' : 'No'}
                  </div>
                  <div className="col-md-6">
                    <strong>Has Salary Info:</strong> {selectedJob.ai_has_salary ? 'Yes' : 'No'}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Job Category:</strong> {selectedJob.ai_taxonomies_a || 'N/A'}
                  </div>
                  <div className="col-md-6">
                    <strong>Job Language:</strong> {selectedJob.ai_job_language || 'N/A'}
                  </div>
                </div>
                {selectedJob.ai_work_arrangement_office_days && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <strong>Office Days:</strong> {selectedJob.ai_work_arrangement_office_days}
                    </div>
                  </div>
                )}
                {selectedJob.ai_remote_location && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <strong>Remote Location:</strong> {selectedJob.ai_remote_location}
                    </div>
                  </div>
                )}
                {selectedJob.ai_working_hours && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <strong>Working Hours:</strong> {selectedJob.ai_working_hours}
                    </div>
                  </div>
                )}
                {selectedJob.ai_hiring_manager_name && (
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Hiring Manager:</strong> {selectedJob.ai_hiring_manager_name}
                    </div>
                    <div className="col-md-6">
                      <strong>Email:</strong> {selectedJob.ai_hiring_manager_email_address || 'N/A'}
                    </div>
                  </div>
                )}
                {selectedJob.ai_key_skills && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <strong>Key Skills:</strong> <div className="text-muted small">{selectedJob.ai_key_skills}</div>
                    </div>
                  </div>
                )}
                {selectedJob.ai_benefits && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <strong>Benefits:</strong> <div className="text-muted small">{selectedJob.ai_benefits}</div>
                    </div>
                  </div>
                )}
                {selectedJob.ai_core_responsibilities && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <strong>Core Responsibilities:</strong> <div className="text-muted small">{selectedJob.ai_core_responsibilities}</div>
                    </div>
                  </div>
                )}
                {selectedJob.ai_requirements_summary && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <strong>Requirements Summary:</strong> <div className="text-muted small">{selectedJob.ai_requirements_summary}</div>
                    </div>
                  </div>
                )}
                {selectedJob.ai_education_requirements && (
                  <div className="row mb-4">
                    <div className="col-12">
                      <strong>Education Requirements:</strong> <div className="text-muted small">{selectedJob.ai_education_requirements}</div>
                    </div>
                  </div>
                )}

                {/* Locations Array */}
                {selectedJob.locations && selectedJob.locations.length > 0 && (
                  <>
                    <h6 className="border-bottom pb-2 mb-3 text-primary">Locations ({selectedJob.locations.length})</h6>
                    {selectedJob.locations.map((loc, idx) => (
                      <div key={idx} className="row mb-2 bg-light p-2 rounded">
                        <div className="col-md-6">
                          <strong>City:</strong> {loc.city || 'N/A'}
                        </div>
                        <div className="col-md-6">
                          <strong>State:</strong> {loc.state || 'N/A'} ({loc.state_code || 'N/A'})
                        </div>
                        <div className="col-md-6">
                          <strong>Country:</strong> {loc.country || 'N/A'} ({loc.country_code || 'N/A'})
                        </div>
                        <div className="col-md-6">
                          <strong>Coordinates:</strong> {loc.latitude && loc.longitude ? `${loc.latitude}, ${loc.longitude}` : 'N/A'}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Job Description */}
                {selectedJob.description && (
                  <>
                    <h6 className="border-bottom pb-2 mb-3 text-primary">Job Description</h6>
                    <div className="mb-3">
                      <div
                        className="p-3 bg-light rounded"
                        style={{
                          maxHeight: '400px',
                          overflowY: 'auto',
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.9em'
                        }}
                        dangerouslySetInnerHTML={{ __html: selectedJob.description }}
                      />
                    </div>
                  </>
                )}

                {/* Raw JSON Data */}
                <h6 className="border-bottom pb-2 mb-3 text-primary">Raw JSON Data</h6>
                <pre className="bg-dark text-light p-3 rounded" style={{ maxHeight: '300px', overflowY: 'auto', fontSize: '0.75em' }}>
                  {JSON.stringify(selectedJob, null, 2)}
                </pre>
              </div>
              <div className="modal-footer">
                {selectedJob.url && (
                  <a
                    href={selectedJob.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <i className="fas fa-external-link-alt me-2"></i>
                    View Original Posting
                  </a>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowJobDetails(false);
                    setSelectedJob(null);
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for CSV import */}
      <input
        ref={csvImportRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleCsvImport}
      />
    </>
  );
};

export default JobListingsPage;
